import { UserRole } from '../models/models'

/**
 * A single "what's new" line item. `roles` scopes the item to the audiences it
 * matters to — an entry only surfaces to a user who holds at least one of the
 * listed roles. Use `ALL_ROLES` for cross-cutting changes.
 */
export interface ChangelogEntry {
  text: string
  roles: UserRole[]
}

/** One released build's changelog: a version, its date, and its entries. */
export interface ChangelogRelease {
  version: string
  date: string
  entries: ChangelogEntry[]
}

/** Convenience for entries that are relevant to every role. */
export const ALL_ROLES: UserRole[] = ['Admin', 'Reviewer', 'Editor', 'Viewer']

/**
 * The application changelog, **newest release first**. Add a new release object
 * at the top of this array on each user-facing deploy; keep the `version` in
 * sync with package.json's `version` so auto-show gating works.
 */
export const CHANGELOG: ChangelogRelease[] = [
  {
    version: '0.26.0',
    date: '2026-11-03',
    entries: [
      {
        text: 'Dialogs now take keyboard focus when they open, keep Tab inside the dialog, and put focus back where you were when they close. A dialog with a text field puts the cursor in it. Escape closes only the dialog on top.',
        roles: [...ALL_ROLES, 'EditorSupervisor'],
      },
      {
        text: 'Tooltips no longer pop up by themselves after you close a dialog, and only one shows at a time. They still appear when you point at a button or move to it with Tab.',
        roles: [...ALL_ROLES, 'EditorSupervisor'],
      },
      {
        text: 'The "How it works" guides, keyboard shortcut lists, tooltips and hints in Segment editing, Quick Edit, Markup and Inspections have been brought up to date with the current panels and keys. The panel tabs now show their key in a tooltip, Undo, Redo and Submit for Review have tooltips, and Quick Edit has a map legend. ? also closes the shortcut list it opened.',
        roles: ['Editor'],
      },
      {
        text: 'Markup: the Confirmed button now shows ✓ and Not present shows ✕, matching the marks on the map. When the Missing Segment tool refuses a click, the banner at the bottom of the map says which rule it broke, and a new hint explains when unconfirmed segments are holding up Complete.',
        roles: ['Editor'],
      },
      {
        text: 'Review: the guide now describes the map colours as they are, the legend\'s dashed line reads "Original (replaced or deleted)", the Complete confirmation says where a Quick Edit task\'s roads go, and the shortcut list includes H (Peek).',
        roles: ['Reviewer'],
      },
      {
        text: 'Admin screens have corrected help text throughout. Cancel now says exactly what is discarded for each task type, CSV import errors name the line in your file, Add User says that no invitation email is sent (set a password with Reset Password), and Delete Version says why it is disabled.',
        roles: ['Admin'],
      },
      {
        text: 'The editor list\'s column headers explain what each figure means, and dates show in a readable format.',
        roles: ['EditorSupervisor'],
      },
      {
        text: 'The shortcut list now includes H (Peek).',
        roles: ['Viewer'],
      },
    ],
  },
  {
    version: '0.25.1',
    date: '2026-11-02',
    entries: [
      {
        text: 'Markup: if you change a missing segment\'s attributes and move on without applying them — clicking something else on the map, another segment, another tab, a tool, Complete or leaving the task — you are now asked whether to Apply, Discard or Keep editing, as in Segment editing. Delete, Undo and Ctrl+Enter still act straight away.',
        roles: ['Editor'],
      },
      {
        text: 'Markup: the banner about unconfirmed segments now updates as you confirm, delete or draw segments, and disappears once none are left.',
        roles: ['Editor'],
      },
      {
        text: 'Markup: Delete works again on an open missing segment after you undo or redo a shape.',
        roles: ['Editor'],
      },
    ],
  },
  {
    version: '0.25.0',
    date: '2026-11-02',
    entries: [
      {
        text: 'Segment editing has a new panel on the right, like the Markup editor\'s, with two tabs. Roads (R) lists every road in your task by name, with To do and Changed filters; Changes (E) lists what you drew, reshaped, split, merged or deleted. Pick a row to zoom to the road and open it. The attributes are always editable: change them and press Apply. Delete, Merge and Reset are at the bottom of the panel instead of in the toolbar.',
        roles: ['Editor'],
      },
      {
        text: 'A road you draw opens straight away, marked "Needs attributes" until you fill them in. If it touches another road, "Start road" and "End road" copy that road\'s attributes. A task with a drawn road that still needs attributes can\'t be submitted for review: a banner tells you how many, and Show lists them.',
        roles: ['Editor'],
      },
      {
        text: 'If you change attributes and then move on without applying them — another road, another tab, a tool, Submit for Review or leaving the task — you are asked whether to Apply, Discard or Keep editing, so typing is no longer lost by accident.',
        roles: ['Editor'],
      },
      {
        text: 'A deleted road is listed in Changes and shown dashed on the map. Open it and press Restore to bring it back as it was; Undo works on Restore too. Deleted roads and your undo history now also survive reloading the page.',
        roles: ['Editor'],
      },
      {
        text: 'New keys in the Segment editing and Quick Edit panels: , and . step to the previous / next road, J jumps to the next one to do, Z zooms to the open road, and Ctrl+Enter (⌘+Enter) applies your changes, even while you are typing. Escape leaves a text field. Hovering a row highlights its road on the map. Undo brings back the road you were working on, and a road stays marked in the list while you draw, split or pan. The panel works with screen readers.',
        roles: ['Editor'],
      },
      {
        text: 'Quick Edit has the same panel with a Changes tab: click a road to see its length and shape details, with Delete and Merge at the bottom, and restore a deleted road from Changes.',
        roles: ['Editor'],
      },
      {
        text: 'Map colours follow your colour settings everywhere: in Segment editing, the faded reference roads, the roads you can edit and the legend all use your palette, and pointing at a road shows a small card with its name and attributes.',
        roles: ['Editor'],
      },
      {
        text: '"Where time goes": discarded attribute edits are now measured against the number of times an attribute panel was shown, and deleting a road or undoing no longer counts as friction.',
        roles: ['EditorSupervisor'],
      },
    ],
  },
  {
    version: '0.24.3',
    date: '2026-11-02',
    entries: [
      {
        text: 'A new inspection type is recognised: "HERE route exists where AccuTerra has none". Bundles that contain it now ingest without needing "Skip unknown inspections".',
        roles: ['Admin'],
      },
      {
        text: 'Markup tasks can include a new inspection type, "HERE route exists where AccuTerra has none": another map source has a road connection where AccuTerra has none.',
        roles: ['Editor'],
      },
    ],
  },
  {
    version: '0.24.2',
    date: '2026-10-27',
    entries: [
      {
        text: 'Inspections now start with a list of projects, each showing its current version and how far it has got. Pick one to review its inspections; the screen\'s title names the project and version, and the progress bar counts that project only. A project with every inspection answered is shown as done. To switch project, go back to the home page and open Inspections again, or use "Choose another project" when a project has nothing left for you.',
        roles: ['Editor'],
      },
      {
        text: '"Where time goes" for Inspections counts each editor\'s activity under the project they picked, and lists picking a project as its own action.',
        roles: ['EditorSupervisor'],
      },
    ],
  },
  {
    version: '0.24.1',
    date: '2026-10-27',
    entries: [
      {
        text: 'The Inspections card on the home page has its button back, now called "Review Inspection", beside the Continue button in Inspection review.',
        roles: ['Editor'],
      },
    ],
  },
  {
    version: '0.24.0',
    date: '2026-10-27',
    entries: [
      {
        text: 'A new Editor home page. Under "Continue" you pick up where you left off: Inspection review (with the last inspection you answered) and your task in progress, each with its own Continue button, plus any task a reviewer sent back for fixes — "Open in task list" takes you straight to it. Below, the Inspections card shows how far the team has got and your own answers, and the Tasks card shows what is available, what you have completed and the area you have covered, with your recently completed tasks. A streak counts the days in a row you have worked. The task list has moved to its own page, reached from "Open task list".',
        roles: ['Editor'],
      },
      {
        text: 'Inspection Groups are now called Inspections. Some projects show one inspection at a time ("inspection 10000"), others a group of several inspections as one ("group 82"). You answer both the same way.',
        roles: ['Editor'],
      },
      {
        text: 'The bar at the top of the screen stays in place while you scroll, and the link back to the role selection reads "Switch Role".',
        roles: ['Editor', 'Reviewer', 'EditorSupervisor'],
      },
      {
        text: 'Ingest reads the new bundle format: every inspection marked for review becomes one item in the Inspections review, with its images taken from the folder the bundle names. Before anything is written, the ingest checks that each of those inspections has its image folder and, if any are missing (for example while the upload is still running), stops and names them — wait for the upload and run it again. The coordinate system now comes from the bundle itself, and a GeoJSON inspection import must name its coordinate system. Bundles in the older format with inspection group tables are no longer accepted.',
        roles: ['Admin'],
      },
      {
        text: 'Inspection answers from projects in the new bundle format are labelled by inspection ("inspection 10000") rather than by group, on the editor\'s Inspection Groups tab and in the group review screen.',
        roles: ['EditorSupervisor'],
      },
    ],
  },
  {
    version: '0.23.0',
    date: '2026-10-20',
    entries: [
      {
        text: 'Markup: the right-hand panel is now one work pane with four tabs — Inspections, Connectors, Segments and Markups — in place of the separate list windows. Each tab shows a list with what is left to do; open an item to see its details, step through with ‹ › or "Next to do →", and go back to the list with the tab\'s letter (I, C, S, A) or Esc. Clicking something on the map opens it in its tab, and pointing at a list row highlights it on the map. Pointing at a road, a missing segment, an inspection or a shape on the map shows a small card next to the pointer with its main details, so the pane no longer changes as you move the mouse.',
        roles: ['Editor'],
      },
      {
        text: 'Markup: suggested connectors. The editor points out places where a road stops just short of another road (the gap is given in feet). Open the Connectors tab (C) and answer each one: Yes – draw it (1) starts a Missing Segment at the road end, No (2) if nothing is missing, and That segment (3) links a segment you already drew there. Draw only obvious roads and short gaps — skip field tracks. As with your other Markup actions, opening the list, your answers and the segments you draw from them are recorded to help us improve the tool.',
        roles: ['Editor'],
      },
      {
        text: 'Markup: a shape\'s note and a missing segment\'s attributes are always editable — no pencil. Notes save as you type; segments are saved with "Apply & confirm" (later "Save changes"). Delete has moved from the toolbar to the bottom of the open markup or segment (the Delete key still works), and the answer and action buttons fit on one row. An Auto-next switch moves you to the next inspection or connector after you answer.',
        roles: ['Editor'],
      },
      {
        text: 'Markup keyboard shortcuts: , and . for the previous and next item, J for the next to do, 1 / 2 / 3 for the answer buttons (each shows its number), Q to flag a markup for Quick Edit, Z to zoom to the item, and Ctrl+Enter (⌘+Enter on a Mac) to apply a segment, even while typing in its fields. In a list, ↑ / ↓ move and Enter opens. Shortcuts never fire while you type in a field; press Esc to leave it. Undo reopens the shape it restores. After a delete, or when another tool or Undo closes the open item, the keyboard focus stays in the work pane and a screen reader hears the list. The ? overlay and the tutorial list every key.',
        roles: ['Editor'],
      },
      {
        text: 'While drawing a new segment (Missing Segment in Markup, Draw in Segment editing and Quick Edit), the nearby road you can snap to is highlighted with its vertices, and a ring marks exactly where your click will land.',
        roles: ['Editor'],
      },
      {
        text: 'Road lengths read in feet up to 1,000 ft, then in miles with two decimals (for example "240 ft", "0.27 mi"). An unnamed road shows "No name" in the Name field, and clearing a name really removes it. The Active Task card on the task list is titled by its tile, like the other cards (for example "002-028 · 9.3 sq mi").',
        roles: ['Editor'],
      },
      {
        text: 'Task areas read in square miles (for example "058-122 · 9.3 sq mi"), and the Quick Edit area around a flagged Markup shape is given as about 0.6 mi on every side. Tasks drawn by hand or imported from a CSV now store their true area, as ingested tiles always did; their areas used to be about 1.7× too large at Colorado latitudes, and existing ones have been corrected.',
        roles: ['Admin', 'Editor', 'Reviewer', 'EditorSupervisor'],
      },
      {
        text: 'The Markup export has a new Suggested Connectors layer: every connector the editor suggested, with its gap, the Editor\'s answer and the missing segment drawn for it.',
        roles: ['Admin'],
      },
      {
        text: 'The Markup task overview lists the task\'s suggested connectors with the Editor\'s answers (yes, no or unanswered) and their gaps in feet; click one to see it on the map. Road lengths read in feet or miles. On "Where time goes", the Markup work pane brings new rows (Work pane navigation, Auto-next toggled, Markup notes saved, Zoom to item); the rows they replace are marked "(old)" and stop growing, so their weekly trends end here.',
        roles: ['EditorSupervisor'],
      },
      {
        text: 'The map\'s Layers panel remembers whether you left it collapsed or expanded.',
        roles: ['Admin', 'Editor', 'Reviewer', 'Viewer', 'EditorSupervisor'],
      },
      {
        text: 'Speed limits show their unit (for example "45 mph").',
        roles: ['Viewer'],
      },
    ],
  },
  {
    version: '0.20.1',
    date: '2026-10-06',
    entries: [
      {
        text: 'Where time goes: actions now read in plain words (for example "Groups shown · preloaded", "Drawings cancelled · switched tool", "Group images loaded · 0 failed") instead of raw values. Slow and failed server requests have moved out of the Actions table into their own collapsed "Waiting on the server" section (also on a task\'s Activity view), and a request timed across a sleeping laptop or a frozen browser tab (over a minute) no longer counts toward its time. Expanded editor comparisons and the Session events list now use the full width, and a single day shows as one date.',
        roles: ['EditorSupervisor'],
      },
    ],
  },
  {
    version: '0.20.0',
    date: '2026-10-06',
    entries: [
      {
        text: 'New "Where time goes" page, linked from the editor list: for a version and a kind of work (Markup, Segment editing, Quick Edit or Inspection Group review), every editor action with how often it happens, its median and slow-end (p90) time, how often it is done from the keyboard, a weekly trend, and for Markup the time our workflow model expects. Expand an action to compare each editor with everyone else: one editor slower than the rest points to training, everyone slower than the model points to the tool. A Friction table counts cancelled and rejected drawings, discarded edits, undos and ignored shortcuts. Updated daily.',
        roles: ['EditorSupervisor'],
      },
      {
        text: 'For Inspection Group review, the same page shows per editor how long a decision takes once the images are on screen, how long the images take to load, how often the next group was already preloaded, and how often groups are skipped or answers undone.',
        roles: ['EditorSupervisor'],
      },
    ],
  },
  {
    version: '0.19.1',
    date: '2026-10-06',
    entries: [
      {
        text: 'Markup: clicking ✏ to edit a shape\'s note now puts the cursor straight in the note box, so you can type right away. Before, the first letters you typed could trigger map shortcuts instead (for example "i" opened the Inspections list). "Edit attributes" on a road segment or missing segment now does the same for the Name field.',
        roles: ['Editor'],
      },
      {
        text: 'The read-only preview of the segment or shape under the pointer now disappears as soon as the pointer leaves the map, instead of staying over the side panel.',
        roles: ['Editor'],
      },
      {
        text: 'Segment editing: picking another tool while holding Space to pan now takes effect when you let go of Space. Before, the map could go back to the previous tool while the toolbar showed the new one. Switching to another window while holding Space (or H for Peek) no longer leaves the map stuck panning or peeking.',
        roles: ['Editor'],
      },
    ],
  },
  {
    version: '0.19.0',
    date: '2026-10-06',
    entries: [
      {
        text: 'Time per tile: the editor list has a Task type picker (Markup by default) and shows, per editor, the tiles completed, the median active time and median wall time per tile, and how that time splits into working, moving the map, viewing and away. Medians appear once an editor has 10 tracked tiles; tiles started before tracking began, worked by more than one editor, or Inspection Group follow-up tasks are listed but not counted. The numbers come from completed tiles and update once a day.',
        roles: ['EditorSupervisor'],
      },
      {
        text: 'Each editor\'s page has a new Time tab: every tracked tile with its active and wall time, map content and rework rounds, plus a learning curve of time per tile across all versions, so you can see whether an editor is getting faster.',
        roles: ['EditorSupervisor'],
      },
      {
        text: 'A task\'s page has a new Activity view next to the map: a timeline of working, moving the map, viewing and away time with markers for key moments, how each action was triggered (keyboard, menu, button or map), and which actions took the most time. It is computed live, so it also works for tasks still in progress.',
        roles: ['EditorSupervisor'],
      },
    ],
  },
  {
    version: '0.18.3',
    date: '2026-10-06',
    entries: [
      {
        text: 'Markup: picking a drawing tool right after finishing a shape now always works. Before, pressing a tool\'s key very quickly after finishing a shape could leave the map selecting instead of drawing, even though the toolbar showed the drawing tool.',
        roles: ['Editor'],
      },
    ],
  },
  {
    version: '0.18.2',
    date: '2026-10-05',
    entries: [
      {
        text: 'The satellite basemaps no longer freeze the page asking for a MapTiler key. If your saved basemap needs a key this browser doesn\'t have yet, the map opens on OpenStreetMap and the Layers panel tells you so, with an Enter key button. You type the key in a proper dialog, and Cancel keeps any key you already saved. If MapTiler rejects a key, the map switches to OpenStreetMap and the Layers panel says why.',
        roles: [...ALL_ROLES, 'EditorSupervisor'],
      },
    ],
  },
  {
    version: '0.18.0',
    date: '2026-10-05',
    entries: [
      {
        text: 'To understand where the app slows you down so we can improve it and the training, the app now keeps a record of how you use the Editor screens (task list, editing, Quick Edit, Markup and Inspection Groups): which tools, buttons and shortcuts you use and whether by mouse or keyboard, what you click on the map, zooming and panning, how long loading, saving and completing take, and a 15-second summary of whether you are working, moving the map or just looking. It never records what you type (notes, attribute values), what is on your screen or your mouse path, and it records none of your input while the app\'s tab is in the background (results of things you already started, such as a save finishing, may still be noted). The data is kept for 60 days and Editor Supervisors can see it.',
        roles: ['Editor'],
      },
      {
        text: 'In Segment editing and Quick Edit, the Delete and Backspace keys now delete the selected segments in Select mode, just like the Delete button. While you are working in the attributes panel (or, in Markup, the note panel) these keys no longer delete anything; click the map first.',
        roles: ['Editor'],
      },
    ],
  },
  {
    version: '0.17.0',
    date: '2026-10-02',
    entries: [
      {
        text: 'New "Needs editing" flag on the Inspection Groups screen. When a place needs a road drawn or fixed by hand, whatever your answer, turn on Needs editing before you answer: press E, or find it in the ••• menu on a touch screen. While it is on, Yes, No and Can\'t tell show a small orange dot, and the flag is saved with your answer. Undo brings the group back with the flag as you saved it.',
        roles: ['Editor'],
      },
      {
        text: 'The Inspection Groups screen now has a "How it works" guide. It opens once on your first visit; press ? or tap the ⓘ button to open it again.',
        roles: ['Editor'],
      },
      {
        text: 'New Markup follow-up tasks, labelled "Markup · Follow-up", hold the places editors flagged Needs editing. Each place is already outlined, with a note naming the group and its answer. Draw any missing road with Missing Segment (N), tick Quick Edit on an outline whose area needs redrawing, or add "No change needed" to its note, then complete the task. The Markup guide has a new Follow-up tasks section.',
        roles: ['Editor'],
      },
      {
        text: 'The Quick Edit checkbox on a Markup shape now says exactly which area the new Quick Edit task covers: the shape\'s surrounding rectangle, extended by about 0.6 mi on every side.',
        roles: ['Editor'],
      },
      {
        text: 'On the Export page, the Inspection Groups panel shows how many answers were flagged Needs editing, and the exported file has a new needs_edit column. Create follow-up tasks turns the flagged groups of the latest version into Markup follow-up tasks, one per tile, unassigned so any editor can start them. Running it again adds only new flags.',
        roles: ['Admin'],
      },
      {
        text: 'The editor roster counts each Editor\'s Needs editing flags, and their Inspection Groups answers show a Needs editing badge.',
        roles: ['EditorSupervisor'],
      },
      {
        text: 'Tooltips now always fit on screen: near an edge they open on the other side or shift inward instead of being cut off.',
        roles: [...ALL_ROLES, 'EditorSupervisor'],
      },
    ],
  },
  {
    version: '0.16.0',
    date: '2026-09-28',
    entries: [
      {
        text: 'On a phone or tablet, Yes and No now sit in the bottom-right corner, always one tap away, with the overlay on/off button right above No. Everything else is behind the ••• button above them. Tap it and each button slides out next to the ones it belongs with: Fullscreen and Undo above, Aerial/Slope beside the overlay button, Skip and Can\'t tell beside Yes. Tap the arrow at the top to fold them away again.',
        roles: ['Editor'],
      },
      {
        text: 'Undo is easier to use when you answer quickly. It now undoes your most recent answer for up to a minute, even after you have moved on to the next group or skipped one. Use the Undo button (in the ••• menu on a phone, next to the answers on a computer) or press U. If your answer is still saving when you press it, Undo waits for the save to finish and then undoes it.',
        roles: ['Editor'],
      },
      {
        text: 'The answer buttons now use simple black-and-white icons instead of coloured emoji, matching the rest of the controls on the image.',
        roles: ['Editor'],
      },
    ],
  },
  {
    version: '0.15.0',
    date: '2026-09-23',
    entries: [
      {
        text: 'The Inspection Groups screen now fits your phone exactly, in portrait and landscape. Every button stays on screen and nothing scrolls away, even right after a reload.',
        roles: ['Editor'],
      },
      {
        text: 'On a phone or tablet the buttons fold away to leave the image clear: three small round buttons sit in the bottom-right corner. Tap the check mark to open the answers (Skip, Can\'t tell, Yes, No); the row folds again once you answer. Tap the layers button to switch between Aerial and Slope and turn the overlay on and off; that row stays open until you close it. All buttons are see-through, so the image shows beneath them.',
        roles: ['Editor'],
      },
      {
        text: 'New fullscreen mode for the Inspection Groups screen. Tap the fullscreen button (or press F) to hide the header, the question and the browser\'s address bar so the image fills the whole screen. Tap it again, press Esc or use your phone\'s Back gesture to return.',
        roles: ['Editor'],
      },
      {
        text: 'Dragging the image past its edge, or zooming past the closest or widest view, now stretches a little and springs back when you let go, so you can tell your gesture was recognised.',
        roles: ['Editor', 'EditorSupervisor'],
      },
      {
        text: 'The Undo message after an answer is now wider on phones, with its buttons on their own row below the text.',
        roles: ['Editor'],
      },
      {
        text: 'On touch screens, tapping a button no longer pops up its tooltip. Press and hold a button to see what it does.',
        roles: [...ALL_ROLES, 'EditorSupervisor'],
      },
    ],
  },
  {
    version: '0.14.0',
    date: '2026-09-17',
    entries: [
      {
        text: 'A new Editor Supervisor role for following Editors\' work without touching it. Pick it on the landing screen, choose a project and version, and see every Editor with their completed and pending tasks, how many Inspection Groups they answered and when they were last active. Open an Editor to page through their tasks. Nothing on these screens claims, edits, answers or exports anything.',
        roles: ['EditorSupervisor'],
      },
      {
        text: 'Open any of an Editor\'s tasks to see the work so far, finished or still in progress. Segment Editing and Quick Edit tasks show the original segments beside the Editor\'s result on a map. Markup tasks list every drawn shape with its note and every inspection item with its answer, and clicking a row zooms the map to it. The Markup map adds imagery layers and a switch for the task\'s AccuTerra road segments, and every task map has Peek (hold H) to see the ground underneath.',
        roles: ['EditorSupervisor'],
      },
      {
        text: 'An Editor\'s Inspection Groups tab lists their answers, newest first, with a filter by answer and a chart of answers per day over the last two weeks. Open an answer to see the images the Editor judged and what they chose, then step through their other answers with Previous and Next (or the comma and period keys).',
        roles: ['EditorSupervisor'],
      },
      {
        text: 'Admins can now give users the Editor Supervisor role on the User Management screen.',
        roles: ['Admin'],
      },
      {
        text: 'Peek\'s menu now has two options you can switch on independently: Keep additional layers (imagery such as NAIP and slope) and Keep data layers (the AccuTerra road segments). With both off, Peek shows just the basemap, as before. Your task\'s own work is always hidden while you peek.',
        roles: ['Editor', 'EditorSupervisor'],
      },
    ],
  },
  {
    version: '0.13.0',
    date: '2026-09-16',
    entries: [
      {
        text: 'The Inspection Groups review screen now shows one large, zoomable image instead of four small thumbnails. Pinch, scroll or double-tap to zoom in on the road you\'re judging, and flip between Aerial and Slope, and Overlay on and off, to check whichever combination makes the call easiest — your zoom and pan carry over when you flip between them. Answering still waits for all four images to be ready behind the scenes.',
        roles: ['Editor'],
      },
      {
        text: 'The review screen now works comfortably on phone and tablet: the Yes / No / Can\'t Tell / Skip buttons are thumb-reachable floating buttons in the corner, and holding a finger on any button shows what it does and its shortcut. The question line can be dismissed to give the image more room.',
        roles: ['Editor'],
      },
      {
        text: 'Answered a group by mistake? An Undo option appears for a few seconds right after you answer (or until you move on) and puts the group back exactly as if you\'d never answered it.',
        roles: ['Editor'],
      },
    ],
  },
  {
    version: '0.12.0',
    date: '2026-09-15',
    entries: [
      {
        text: 'A new Inspection Groups review screen. Open it with Review Inspection Groups at the top of your task list. It shows one group of related road-connection inspections at a time as four images: aerial and slope, each with and without the drawn roads. It asks one question: is the path highlighted in red a real road? Answer Yes, No or Can\'t tell with the buttons or the Y, N and C keys. If you can\'t judge a group, Skip it (S) and it goes to the back of the queue. The next group is already loaded, so you can move quickly. All editors share one queue, and no two of you are ever shown the same group at once.',
        roles: ['Editor'],
      },
      {
        text: 'Answers are final, so the review screen guards against accidental ones. The buttons unlock only once all four images have loaded, a double press can\'t answer two groups, and the keys do nothing while a dialog or the account menu is open. If you step away for more than nine minutes, the screen pauses and hands your groups back to the queue. Continue picks up where you left off.',
        roles: ['Editor'],
      },
      {
        text: 'Ingest now also loads the Inspection Groups that come with a bundle. These are the clusters of related connector and long-gap inspections that the production team prepares review images for, and the ingest summary shows how many were loaded. Colorado v2 carries 33,564 of them. Markup tasks and their inspections are unchanged.',
        roles: ['Admin'],
      },
      {
        text: 'The Export screen has a new Inspection Groups section. For the selected project version it shows how many groups were answered Yes, No and Can\'t tell and how many are still open. It exports every group, answered or not, with its answer, who gave it and when, as a GeoPackage for the production team.',
        roles: ['Admin'],
      },
      {
        text: 'Deleting a project version now warns you if it would destroy Inspection Group answers that are not in any export yet, and offers a button to export them on the spot. The warning does not stop the deletion; the choice is yours.',
        roles: ['Admin'],
      },
      {
        text: 'Exports are more reliable. A file too big to send directly used to open in a new browser tab after the export finished, and a pop-up blocker could silently stop that while the screen still said Exported. It now downloads in place. A failed export now says what went wrong instead of "An unexpected error occurred". Large GeoPackages are also built far faster: a whole-state Markup export that used to run out of time now finishes.',
        roles: ['Admin'],
      },
    ],
  },
  {
    version: '0.11.0',
    date: '2026-09-14',
    entries: [
      {
        text: 'Completing an Edit task that reshapes, deletes or splits existing roads works again. Since early June these completions failed with "An unexpected error occurred" and the task stayed In Review. Only tasks made entirely of newly drawn roads could be completed. The edits in those tasks are still staged, so they can now be completed normally, and nothing needs redrawing. Roads changed this way also keep their original speed limit and restrictions.',
        roles: ['Admin', 'Reviewer', 'Editor'],
      },
      {
        text: 'Markup tasks now show the new inspection types from the latest Colorado data: connector roads added from CDOT data, long gaps bridged between road ends, one-way directions that disagree with CDOT, divided highways whose direction could not be worked out, and checks on where tunnels were placed. Each has a plain-language title in the inspection list, like the existing types.',
        roles: ['Admin', 'Editor'],
      },
      {
        text: 'Ingest now accepts the new AccuTerra bundle format, starting with Colorado v2, and Ingest from S3 finds bundles in their new location. A bundle in the old format is refused straight away with a message saying so, instead of being partly ingested.',
        roles: ['Admin'],
      },
      {
        text: 'The Ingest from S3 list loads again. It had started failing with an error once the production team began publishing inspection images next to the bundles.',
        roles: ['Admin'],
      },
      {
        text: 'Large ingests are much faster. The Colorado v2 bundle, over 610,000 roads, now ingests in about three minutes, where before it ran out of time partway through. The zoomed-out road map also waits for a running ingest to finish before refreshing, instead of slowing it down.',
        roles: ['Admin'],
      },
    ],
  },
  {
    version: '0.10.0',
    date: '2026-08-21',
    entries: [
      {
        text: 'Deleting a project version now runs in the background instead of timing out. Large versions used to fail outright — the request gave up after ten seconds and the version stayed exactly where it was, however many times you tried. Deletion now starts a job on the server, and the panel shows what it is working through stage by stage, with a progress bar and an elapsed clock. A statewide version takes a few minutes; smaller ones are still gone in seconds.',
        roles: ['Admin'],
      },
      {
        text: 'You no longer have to sit and watch it finish. Close the panel, move to another screen, or reload the page — the deletion carries on regardless, the version keeps a "Deleting…" marker in the list, and coming back picks the progress up wherever it has actually got to. Only the version being deleted is held; the rest stay available.',
        roles: ['Admin'],
      },
      {
        text: 'If the app loses contact with a running deletion it now says so, instead of reporting a failure. The work is on the server, so losing the page never stops it — "lost contact", with a Check again button, is the honest description, where "deletion failed" would invite you to re-run something that is still going.',
        roles: ['Admin'],
      },
      {
        text: 'The deletion panel now loads for very large project versions instead of failing. It counts every road segment, task and inspection item in the version before it can show you the summary, and on the biggest ones that was taking longer than the server was willing to wait.',
        roles: ['Admin'],
      },
      {
        text: 'Very large task exports now download properly rather than failing. Anything above roughly 4.5 MB is handed back as a download link instead of being sent inline, which is what it should always have done.',
        roles: ['Admin'],
      },
    ],
  },
  {
    version: '0.9.1',
    date: '2026-08-21',
    entries: [
      {
        text: 'The Export button on the project-version deletion panel now does what it says. Deleting a project version requires every completed Markup and Quick Edit task in it to have been exported first, and the panel lists the ones that still need it with a button beside each — but exporting never cleared the entry, so the list never shrank and the version could not be deleted no matter how many times you exported. Exporting a task now clears its entry, and a whole-project export from the Export screen clears them all at once. Anything you exported before this release needs exporting once more, because nothing was recorded at the time.',
        roles: ['Admin'],
      },
      {
        text: 'Exporting a completed Markup or Quick Edit task from the task list also counts towards that requirement now — it is the same export, so it no longer matters which screen you run it from.',
        roles: ['Admin'],
      },
    ],
  },
  {
    version: '0.9.0',
    date: '2026-08-20',
    entries: [
      {
        text: 'Zoomed-out views of the road network are complete again, and faster. Below zoom 11 the map left out shorter roads — which are exactly the pieces that connect everything else — so a wide view showed the network as scattered fragments. Wide views are now drawn from a prepared copy of the whole network: every road is there, and the tiles are smaller and quicker than the partial ones they replace.',
        roles: ALL_ROLES,
      },
      {
        text: 'Tasks are now identified by where they sit in the project grid instead of by a fragment of their id. A card that read "0005c943" now reads "058-122" — its row and column counted from the north-west corner of the project — and the task list is ordered to match, so working down the list walks across the map instead of jumping around it. On a project the size of the Colorado one, nearly seven thousand tiles, the old id told you nothing about where a task was. Tasks that are not grid tiles, such as ones drawn by hand, still show the short id.',
        roles: ['Admin', 'Editor'],
      },
      {
        text: 'Clicking a task card now takes you to the task even when it is a speck on the map. Clicking only ever re-centred the map, which from a whole-state view moved it a few pixels and looked like nothing had happened. The first click still just re-centres; click the same card again and the map zooms to that tile.',
        roles: ['Admin', 'Editor'],
      },
    ],
  },
  {
    version: '0.8.0',
    date: '2026-08-19',
    entries: [
      {
        text: 'The AccuTerra road layer no longer goes blank. When several tiles were requested at once they could time out, and a tile that failed once stayed empty for the rest of the session — reloading the page was the only way to get it back. Tiles are now requested a few at a time, retried once if they fail, and while you pan the ones for the view you are actually looking at are served first.',
        roles: ALL_ROLES,
      },
      {
        text: 'Wide views of the road network now draw only the longer roads. Zoomed out over a whole state the map was asking for every road of every project at once — more data than could be sent at all, which is what made those views hang. From zoom 11 in you always see the complete network; below that, short roads are left out. Editing and review always work from the full data, never from what the map happens to draw.',
        roles: ALL_ROLES,
      },
      {
        text: 'Picking a Label attribute in the layer settings now actually labels the map. Labels were only drawn from zoom 14 — several levels further in than the map is normally browsed — so choosing an attribute appeared to do nothing at all. They now appear from zoom 11, and while you are still below that, the setting says so.',
        roles: ALL_ROLES,
      },
      {
        text: 'Your saved map colours and layer settings stick. Saving a colour palette had been failing for everyone, and Viewer accounts could neither load nor save their basemap, opacity and label choices — those quietly reset to the defaults on every visit.',
        roles: ALL_ROLES,
      },
      {
        text: 'Task lists load faster and carry far less data. Each list now asks only for the task states it actually shows, and the response is compressed on the way. The full list had grown to within a hair of the largest response the server can return, at which point it would have begun failing outright.',
        roles: ['Admin', 'Reviewer', 'Editor'],
      },
      {
        text: 'Large bundles now ingest. At the size the production team publishes today — hundreds of megabytes and hundreds of thousands of road segments — an ingest would run out of memory partway through, or fail while copying the bundle out of AccuTerra storage. The bundle is now read a row at a time rather than loaded whole, the copy runs inside the job where it has time to finish, and the slowest step of the ingest went from never finishing to a few seconds.',
        roles: ['Admin'],
      },
      {
        text: 'An ingest survives a reload. Reloading the page or navigating away during a long ingest used to lose the progress view permanently, even though the ingest itself carried on in the background — the only way to learn the outcome was to check Road Segments Management afterwards. Returning to the Ingest screen now picks the run back up, and a run whose worker died is reported as failed instead of spinning forever.',
        roles: ['Admin'],
      },
      {
        text: 'Ingest from S3 tells you straight away when it cannot start. A bundle that has been removed from AccuTerra storage now fails immediately with a clear message instead of becoming a job that dies minutes later, and starting the same bundle a second time takes you to the run already in progress rather than starting a duplicate.',
        roles: ['Admin'],
      },
      {
        text: 'A failed ingest no longer leaves the database working on it. An ingest that ran out of time or memory could leave its query running for over an hour afterwards, slowing everything else down and blocking deploys. Both the ingest service and the command-line tool now stop the database side as well.',
        roles: ['Admin'],
      },
    ],
  },
  {
    version: '0.7.0',
    date: '2026-08-06',
    entries: [
      {
        text: 'Starting a missing segment at a junction now picks the road you are pointing along, and picks the same one every time. Where several roads meet at a single point, the segment your new line attaches to used to be chosen arbitrarily — two clicks that looked identical could attach to different roads. That choice is recorded as the segment\'s starting road, so it matters after the fact, not just on screen.',
        roles: ['Editor'],
      },
      {
        text: 'Fixed a snap that could be silently missed. A vertex sitting almost exactly at the edge of the snap distance was sometimes skipped, so the cursor refused to snap to a road that was plainly in range — with no indication why.',
        roles: ['Editor'],
      },
      {
        text: 'Drawing missing segments stays responsive on dense tasks. Each mouse move used to compare the cursor against every vertex in the whole task, which showed up as a sticky, lagging cursor on a task holding a few thousand road segments.',
        roles: ['Editor'],
      },
      {
        text: 'Dragging the map while editing is smoother. In modify mode every vertex handle was being rebuilt on each frame of a pan, even though nothing about the handles had changed.',
        roles: ['Editor'],
      },
      {
        text: 'Task lists open faster, and hovering a row or a task on the mini-map no longer stutters. The per-row badges and progress figures are now worked out once when the list loads rather than repeatedly while you scroll, and row tooltips are built the first time you hover instead of for every row up front.',
        roles: ['Admin', 'Editor', 'Reviewer'],
      },
      {
        text: 'A burst of activity no longer makes the task list reload over and over. When several tasks change at once — during a batch ingest, or simply a busy afternoon — the list used to start a fresh reload for every single change; it now settles and reloads once.',
        roles: ['Admin', 'Editor', 'Reviewer'],
      },
      {
        text: 'The browse map is lighter on your machine. Hovering over segments to read their attributes used to leave the app doing continuous background work for as long as the sidebar had something in it, even with the mouse held still.',
        roles: ['Viewer'],
      },
      {
        text: 'The app loads faster on first visit. A styling library the app never actually used, and a web font downloaded from another server for the sake of a single icon, have both been removed from what has to arrive before the page can draw.',
        roles: ALL_ROLES,
      },
      {
        text: 'Deleting a project version is faster, and so is everything that waits on it. Several database lookups behind deletion and behind the task lists were scanning entire tables where they can now go straight to the rows they need.',
        roles: ['Admin'],
      },
      {
        text: 'The service behind the task list had been running close enough to its memory limit that it would have begun failing as more tiles were ingested. It now has room to grow.',
        roles: ['Admin'],
      },
      {
        text: 'Listing the available bundles on the Ingest from S3 screen no longer gives up early. The screen could report a failure while the listing it had asked for was still on its way back.',
        roles: ['Admin'],
      },
    ],
  },
  {
    version: '0.6.1',
    date: '2026-08-05',
    entries: [
      {
        text: 'Files that carry elevation values no longer fail. A GeoJSON exported from a GIS pipeline, or a GeoPackage bundle, often includes a height on every coordinate; importing or ingesting one used to stop with an unexplained failure and nothing created. Elevation is now discarded — nothing in the app uses it — and the rest of the file goes through normally. This affects both the Import screen\'s inspection upload and the Ingest screens.',
        roles: ['Admin'],
      },
    ],
  },
  {
    version: '0.6.0',
    date: '2026-08-05',
    entries: [
      {
        text: 'Ingest from S3 no longer leaves you guessing why a button is greyed out. Every disabled control now explains itself on hover — "Select at least one bundle from the table below", or "A batch is already running — stop it or wait for it to finish" — so you can tell the difference between something you need to do and something you need to wait for.',
        roles: ['Admin'],
      },
      {
        text: 'A batch ingest now ends with a one-line summary — "Ingested 3 bundles · 1 skipped · 1 failed." — instead of a table of per-row badges to count by eye, and links straight to Manage Tasks and Manage Projects so you can see what it created.',
        roles: ['Admin'],
      },
      {
        text: 'Ingesting an uploaded bundle no longer dead-ends on its own screen either: the result card now offers "View in Manage Tasks" and "View in Manage Projects" alongside "Ingest Another Bundle".',
        roles: ['Admin'],
      },
      {
        text: 'The ingest result now says what each number counts. "Tasks" in particular was hiding the most consequential thing the operation does — a Markup task is created for every non-excluded tile, and editors can pick them up immediately — which is now stated outright beneath the figures. The "Pruned" stat has been removed; it was always zero and only suggested that pruning was happening.',
        roles: ['Admin'],
      },
      {
        text: 'The "Skip unknown inspections" option now tells you what skipping costs before you commit to it: the dropped rows are the ones counted under "Skipped" in the result, and the only way to get them back is to ingest the bundle again.',
        roles: ['Admin'],
      },
      {
        text: 'Publish dates on the S3 bundle list now read as "2 weeks ago" with the exact timestamp on hover, matching every other admin list. When the bucket has no bundles at all, the empty screen now explains that Production publishes them and points you at the manual upload route instead of just stating that nothing was found.',
        roles: ['Admin'],
      },
      {
        text: 'Corrected some out-of-date guidance on the Markup surface: the tutorial still told you to click "Save" when the button has been "Apply" for a while, it never mentioned the S shortcut for listing the missing segments you have drawn, and the Apply tooltip claimed your work stays private until a reviewer approves it — Markup tasks have no reviewer step, and missing segments are saved for good.',
        roles: ['Editor'],
      },
    ],
  },
  {
    version: '0.5.1',
    date: '2026-07-24',
    entries: [
      {
        text: 'Toolbars no longer run off the edge of the screen on smaller laptops. The buttons that finish your work — Complete, Submit for Review, Create Task — are now pinned to the right and always reachable. Previously, on a 13" or 14" screen the toolbar could be wider than the window and quietly cut them off, leaving no way to finish a task.',
        roles: ALL_ROLES,
      },
      {
        text: 'The editing toolbars now adapt as the window narrows: button labels shorten to just their icons (the full name is still there on hover), and on very narrow windows the tutorial and keyboard-shortcut buttons tuck into the "⋯" menu. If space runs out entirely, the tools scroll sideways so nothing is ever out of reach.',
        roles: ['Editor'],
      },
    ],
  },
  {
    version: '0.5.0',
    date: '2026-07-10',
    entries: [
      {
        text: 'The Export screen now tells you what you will download before you click: export buttons state how many tasks the file will contain (e.g. "Export 106 Markup Tasks") and are disabled — with an explanation on hover — when the export would be empty, the status table marks exactly which rows end up in the file, and a "What\'s inside the GeoPackage?" panel lists the layers. After an export you get a confirmation naming the downloaded file.',
        roles: ['Admin'],
      },
      {
        text: 'The Export page also explains its place in the pipeline: exports are the deliverable fed back to production, and each completed Markup or Quick Edit task must be exported once before its project version can be deleted in Manage Projects.',
        roles: ['Admin'],
      },
      {
        text: 'The two Import flows are finally easy to tell apart: each sits in its own card with a one-line "when to use this" opener, plus "What should the CSV / GeoJSON contain?" panels documenting the expected file format (the sample CSV download now lives there too).',
        roles: ['Admin'],
      },
      {
        text: 'Importing is predictable: before you click you see exactly what will be created and where (e.g. "Creates 3 new Markup tasks — find them under \'No project\' in Manage Tasks"), the inspection import restates its target project/version and updates it live if you change the selection, and result cards link straight to Manage Tasks.',
        roles: ['Admin'],
      },
      {
        text: 'No more dead ends on Import: the GeoJSON upload zone says "Select a project / version first" right inside it (clicking it takes you to the selector), disabled Import buttons explain why on hover, a file with nothing importable says so, and a file that can\'t be read gets an honest "Couldn\'t read file" message instead of "Import Failed".',
        roles: ['Admin'],
      },
      {
        text: 'Import and Export now share one project/version picker that shows which version is the latest and when it was ingested (e.g. "gunnison_national_forest v2 · latest · ingested 2 weeks ago"), so the two screens read the same.',
        roles: ['Admin'],
      },
    ],
  },
  {
    version: '0.4.0',
    date: '2026-07-09',
    entries: [
      {
        text: 'The task list is much easier to navigate: every card now has a name — a short id plus its area (e.g. "04f8 · 9.3 sq mi") — a map legend explains what the tile colours mean, and a one-time hint shows how the map and the list link together (click a tile to jump straight to its card; a small label follows your cursor over the tiles).',
        roles: ['Editor', 'Admin'],
      },
      {
        text: 'No more mystery greyed-out buttons on the task list: when you already have an active task, Start buttons explain the one-task-at-a-time rule right on hover, and the active-task card says how to free yourself up.',
        roles: ['Editor'],
      },
      {
        text: 'Project groups tell you more at a glance: the header badge breaks tasks down by status (e.g. "74 available · 1 needs fixes"), a progress bar shows how much of each project is completed, and single-project lists open automatically. Group headers and cards are fully keyboard-navigable.',
        roles: ['Editor', 'Admin'],
      },
      {
        text: 'Dates on lists are now readable at a glance — "3 weeks ago" instead of a full timestamp — with the exact date and time still available on hover.',
        roles: ['Editor', 'Admin'],
      },
      {
        text: 'The Task Management screen shows who is doing what: In Progress and In Review cards name the editor or reviewer working on them, and the Delete, Cancel and Export buttons explain exactly what will happen before you click. Confirmation dialogs now name tasks readably instead of quoting long IDs.',
        roles: ['Admin'],
      },
      {
        text: 'Creating a task is self-explanatory: toolbar buttons have plain-language tooltips (including why "Create Task" is disabled), a hint walks you through drawing the boundary, and existing task boundaries are now drawn on the map — with a legend — so you can spot overlaps before they block editors.',
        roles: ['Admin'],
      },
      {
        text: 'The dashboard\'s "In Progress" and "In Review" tiles now open the task list pre-filtered to that status, and the "stale" warnings explain what stale means. Smaller touches: friendlier loading and empty states across the task lists, a results count in the filter bar, readable ingest dates on Road Segments Management, and clearer guidance on User Management.',
        roles: ['Admin'],
      },
      {
        text: 'Smaller editor touches: the Peek button explains its hold-to-peek gesture, the map legend notes that you can only edit inside the task boundary, and the attribute panel button is now labelled "Apply" to match how staged edits actually work.',
        roles: ['Editor'],
      },
    ],
  },
  {
    version: '0.3.0',
    date: '2026-07-08',
    entries: [
      {
        text: 'The editors are much easier to pick up: hover any toolbar button for a plain-language tooltip that names it, shows its shortcut and explains what it does — including, for a greyed-out button like Delete or Merge, why it is disabled and how to enable it. A dismissible hint in the map corner guides you from your first click through editing to submitting, and the empty attributes panel now tells you where to start.',
        roles: ['Editor'],
      },
      {
        text: 'The editors now open in Select mode, so clicking a road selects it right away instead of panning the map.',
        roles: ['Editor'],
      },
      {
        text: 'A new map legend on the segment and markup editors explains what each colour means — reference roads, your edits, missing segments, inspection points and the work-area boundary.',
        roles: ['Editor'],
      },
      {
        text: 'You can always see whether your work is saved: a "Saving… / All changes saved" indicator now sits in the toolbar, and submitting or completing a task shows a short confirmation of what happens next.',
        roles: ['Editor'],
      },
      {
        text: 'The task bar now shows a readable task name — project, version and area — instead of a raw ID.',
        roles: ['Editor', 'Reviewer'],
      },
      {
        text: 'Smaller touches: Redo now works from the keyboard (Ctrl/Cmd+Shift+Z or Ctrl+Y) and is listed in the shortcuts panel; the first-run tutorial no longer opens hidden behind the "What\'s New" panel; and map colour settings moved into a "More" menu to declutter the toolbar.',
        roles: ['Editor'],
      },
    ],
  },
  {
    version: '0.2.0',
    date: '2026-07-06',
    entries: [
      {
        text: 'Reviewing inspection items in the Markup editor is smoother: a single, always-visible "New / Confirmed / Not present" control replaces the old toggles, a "Next inspection" button — with a live remaining counter and an optional auto-advance — steps you through unanswered items, and the toolbar buttons now show their keyboard shortcuts.',
        roles: ['Editor'],
      },
      {
        text: 'The Peek button is now a split-button. Alongside the original "hide everything to reveal the basemap" gesture, a new "Keep additional layers" mode hides only the data layers so your imagery, hillshade, slope, and probability rasters stay visible. Your choice is remembered per browser and applies to every editing map and the reviewer\'s peek shortcut.',
        roles: ['Editor', 'Reviewer'],
      },
      {
        text: 'On the editing screens, any open dialog now closes with the Escape key, and map or tool keyboard shortcuts no longer fire while a dialog is open.',
        roles: ['Editor', 'Reviewer'],
      },
    ],
  },
  {
    version: '0.1.0',
    date: '2026-07-03',
    entries: [
      {
        text: 'You can now digitize missing road segments directly in the Markup editor — draw mid-road gaps or missing tile-edge roads with automatic vertex and task-boundary snapping, snap-to-finish, and one-click "copy attributes from the start/end segment" helpers.',
        roles: ['Editor'],
      },
      {
        text: 'The Markup editor now flags unanswered inspection items and shows their status in the toolbar, so nothing is missed before you complete a task.',
        roles: ['Editor'],
      },
      {
        text: 'The read-only Viewer gained project navigation, keyboard shortcuts (Z / R / D), and a streamlined "Demo Areas" toolbar.',
        roles: ['Viewer'],
      },
      {
        text: 'Admins can now delete users — removed from sign-in and hidden from assignment lists.',
        roles: ['Admin'],
      },
      {
        text: 'Introduced the "What\'s New" panel — open it any time from the user menu to review recent changes relevant to your role.',
        roles: ALL_ROLES,
      },
      {
        text: 'The app now detects when a newer version has been deployed and prompts you to refresh so you always run the latest build.',
        roles: ALL_ROLES,
      },
    ],
  },
]
